// BrimLight/client/ui/MediaSelector.js
import { ComponentBase } from "../core/ComponentBase.js";

export class MediaSelector extends ComponentBase {
    static styles = `
        .media-selector-wrapper {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
        }
        .media-preview-container {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            min-height: 50px;
            padding: 0.5rem;
            border: 1px solid var(--bs-border-color-translucent);
            border-radius: var(--bs-border-radius);
        }
        .media-preview-item {
            position: relative;
            width: 80px;
            height: 80px;
        }
        .media-preview-item img, .media-preview-item video {
            width: 100%;
            height: 100%;
            object-fit: cover;
            border-radius: var(--bs-border-radius-sm);
        }
        .media-preview-item .icon-placeholder {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: var(--bs-tertiary-bg);
            border-radius: var(--bs-border-radius-sm);
            font-size: 2rem;
            color: var(--bs-secondary-color);
        }
        .remove-media-btn {
            position: absolute;
            top: -5px;
            right: -5px;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background-color: var(--bs-danger);
            color: white;
            border: none;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.8rem;
            line-height: 1;
            cursor: pointer;
        }
    `;

    constructor(config) {
        super(config);
        this.selectedValue = this.config.maxFiles > 1 ? [] : null;
        this.mediaManager = null;
        this.applyStyles();
    }

    async initialize() {
        // Lazily create the MediaManager instance when needed
        await super.initialize();
    }

    async handleSelectMedia() {
        if (!this.mediaManager) {
            this.mediaManager = await this.createManagedComponent("MediaManager", document.body, {
                instanceId: `${this.config.instanceId}-manager`,
            });
        }

        try {
            const selectedFiles = await this.mediaManager.show({
                category: this.config.category || 'image',
                maxFiles: this.config.maxFiles || 1,
            });

            if (this.config.maxFiles > 1) {
                this.selectedValue = [...(this.selectedValue || []), ...selectedFiles.map(f => f.relativePath)];
            } else {
                this.selectedValue = selectedFiles[0]?.relativePath || null;
            }
            
            this.renderPreview();
            this.container.dispatchEvent(new Event('change', { bubbles: true }));

        } catch (error) {
            if (error !== 'cancelled') {
                this.log.error("Error during media selection:", error);
            }
        }
    }

    render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'media-selector-wrapper';
            this.container.innerHTML = `
                <div class="media-preview-container"></div>
                <button type="button" class="btn btn-outline-secondary header-btn select-media-btn">
                    <i class="bi bi-folder2-open me-2"></i> ${this.t('Browse Media Library')}
                </button>
            `;
            this.container.querySelector('.select-media-btn').addEventListener('click', () => this.handleSelectMedia());
        }
        this.renderPreview();
        return this.container;
    }

renderPreview() {
        const previewContainer = this.container.querySelector('.media-preview-container');
        if (!previewContainer) return;
        previewContainer.innerHTML = '';

        const values = Array.isArray(this.selectedValue) ? this.selectedValue : (this.selectedValue ? [this.selectedValue] : []);

        if (values.length === 0) {
            previewContainer.innerHTML = `<span class="text-muted small">${this.t('No media selected.')}</span>`;
            return;
        }

        values.forEach((relativePath, index) => {
            const item = document.createElement('div');
            item.className = 'media-preview-item';
            
            let previewHtml = `<div class="icon-placeholder"><i class="bi bi-file-earmark"></i></div>`;
            
            const fullUrl = `/${relativePath}`;

            if (/\.(jpg|jpeg|png|gif|webp)$/i.test(relativePath)) {
                previewHtml = `<img src="${fullUrl}" alt="Preview">`;
            } else if (/\.(mp4|webm|mov)$/i.test(relativePath)) {
                previewHtml = `<video src="${fullUrl}" muted playsinline></video>`;
            } else if (/\.(mp3|wav|ogg)$/i.test(relativePath)) {
                previewHtml = `<div class="icon-placeholder"><i class="bi bi-file-earmark-music"></i></div>`;
            }

            item.innerHTML = `
                ${previewHtml}
                <button type="button" class="remove-media-btn" data-index="${index}">&times;</button>
            `;

            item.querySelector('.remove-media-btn').addEventListener('click', (e) => {
                e.stopPropagation();
                if (Array.isArray(this.selectedValue)) {
                    this.selectedValue.splice(index, 1);
                } else {
                    this.selectedValue = null;
                }
                this.renderPreview();
                this.container.dispatchEvent(new Event('change', { bubbles: true }));
            });

            previewContainer.appendChild(item);
        });
    }

    getValue() {
        return this.selectedValue;
    }

    setValue(value) {
        this.selectedValue = value;
        if (this.container) {
            this.renderPreview();
        }
    }

    destroy() {
        this.mediaManager?.destroy();
        super.destroy();
    }
}